import PageTitle from '../components/PageTitle';

import Q1ObjectivesPolicesScorecard2024 from '../components/content/tables/Objectives-q1';

import Section from '../components/Section';
import { InlineClippedImage } from '../components/InlineClippedImage';
import ContentTypeNav from 'components/ContentTypeNav';

import Connell_2017_Melanies_Place_PLACEHOLDER from '../assets/images/content/Connell_2017_Melanies_Place_PLACEHOLDER.jpg';
import Woods_and_Bridge_9x12_300 from '../assets/images/content/Woods_and_Bridge_9x12_300.jpg';
import Connell_2013_Ascend from '../assets/images/content/Connell_2013_Ascend.jpg';

const Q1ObjectivesPolicies = () => {
	return (
		<>
			<PageTitle title="Objectives &amp; Policies" />
			<div className="bg-[#C9B815] py-12 md:py-24">
				<div className="container max-w-[1068px]">
					<div className="md:max-w-[739px]">
						<ContentTypeNav type="objectives" />
						<p className="font-late-serif text-[1.65rem] leading-[1.6] mb-6">Consistent achievement of superior results requires that our people understand Progressive&rsquo;s objectives and their specific roles, and that their personal objectives dovetail with Progressive&rsquo;s. Our objectives are ambitious, yet realistic. Progressive monitors its financial policies continuously and strives to meet these targets annually. Experience always clarifies objectives and illuminates better policies. We constantly evolve as we monitor the execution of our policies and progress toward achieving our objectives.</p>
					</div>
				</div>
			</div>
			<div className="py-12 md:py-24">
				<div className="container max-w-[1068px]">
					<div className="max-w-[740px]">
						<h2 className="font-trade-compressed uppercase m-0 text-[1.875rem] font-bold tracking-widest text-[#4B7B9D]">Objectives</h2>
						<p>
							<span className="text-[#F26721] heading-two">Profitability&nbsp;</span> Progressive&apos;s most important goal is for our insurance subsidiaries to produce an aggregate calendar-year underwriting profit of at least 4%. Our business is a composite of many product offerings defined in part by product type, distribution channel, geography, customer tenure, and underwriting grouping. Each of these products has targeted operating parameters based on level of maturity, underlying cost structures, customer mix, and policy life expectancy. Our aggregate goal is the balanced blend of these individual performance targets in any calendar year.
						</p>
						<p>
							<span className="text-[#F26721] heading-two">Growth&nbsp;</span> Our goal is to grow as fast as possible, constrained only by our profitability objective and our ability to provide high-quality customer service. Progressive is a growth-oriented company and management incentives are tied to profitable growth.
						</p>
						<p>Aggregate expense ratios and growth rates disguise the true nature and performance of each business. As such, we report Personal Lines and Commercial Lines business results separately. We further break down our Personal Lines’ results by vehicle insurance and property insurance with a further break down of our vehicle insurance by channel (agency and direct). This additional level of detail will provide shareholders a clearer picture of the business dynamics of our Personal Lines products.</p>
					</div>
				</div>
			</div>
			<Section backgroundColor="#FEF4F3">
				<div className="container max-w-container-lg">
					<div className="w-full">
						<InlineClippedImage
							src={Woods_and_Bridge_9x12_300}
							alt="Art"
							dimensions={{
								width: 1728,
								height: 1944,
							}}
							clipPaths={[
								{
									initial: {
										x: '12%',
										y: '20%',
										width: '35%',
									},
									expanded: {
										x: '0%',
										y: '0%',
										width: '100%',
									},
								},
							]}
						/>
					</div>
				</div>
				<div className="py-12 md:py-24">
					<div className="md:grid grid-cols-[1fr_1fr] gap-12 px-4 sm:px-6">
						<div className="md:max-w-[520px] ml-auto col-start-1">
							<h2 className="font-trade-compressed uppercase m-0 text-[1.875rem] font-bold tracking-widest text-[#4B7B9D]">Financial Policies</h2>
							<p>Progressive balances operating risk with risk of investing and financing activities in order to have sufficient capital to support all the insurance we can profitably underwrite and service. Risks arise in all operational and functional areas, and, therefore, must be assessed holistically, accounting for the offsetting and compounding effects of the separate sources of risk within Progressive.</p>
						</div>
					</div>
					<div className="md:grid grid-cols-[1fr_1fr] gap-12 px-4 sm:px-6">
						<div className="md:max-w-[520px] ml-auto col-start-1">
							<p>We use risk management tools to quantify the amount of capital needed, in addition to surplus, to absorb consequences of events such as unfavorable loss reserve development, litigation, weather-related catastrophes, and investment-market corrections. Our financial policies define our allocation of risk and we measure our performance against them. We will invest capital in expanding business operations when, in our view, future opportunities meet our financial objectives and policies. Under-leveraged capital will be returned to investors. We expect to earn a return on equity greater than its cost. Presented is an overview of Progressive&apos;s Operating, Investing, and Financing policies.</p>
						</div>
						<div className="md:max-w-[520px]  flex flex-col gap-12">
							<div>
								<p className="mb-0">
									<span className="text-[#F26721] font-late-serif font-wght-700">Operating&nbsp;</span> Maintain pricing and reserving discipline
								</p>
								<ul className="pb-0 mt-0 mb-0 ml-5 list-disc">
									<li>Manage profitability targets and operational performance at our lowest level of product definition</li>
									<li>Sustain premiums-to-surplus ratios at efficient levels, and at or below applicable state regulations, for each insurance subsidiary</li>
									<li>Ensure loss reserves are adequate and develop with minimal variance</li>
								</ul>
							</div>

							<div>
								<p className="mb-0">
									<span className="text-[#F26721] font-late-serif font-wght-700">Investing&nbsp;</span> Maintain a liquid, diversified, high-quality investment portfolio
								</p>
								<ul className="mt-0 mb-0 ml-5 list-disc">
									<li>Manage on a total return basis</li>
									<li>Manage interest rate, credit, prepayment, extension, and concentration risk</li>
									<li>
										<p className="mb-[0.5rem]">Allocate portfolio between two groups:</p>
										<p className="mb-2">
											<em>Group I:</em> Target 0% to 25% (common equities; nonredeemable preferred stocks; redeemable preferred stocks, except for 50% of investment-grade redeemable preferred stocks with cumulative dividends; and all other non-investment-grade fixed-maturity securities)
										</p>
										<p className="mb-0">
											<em>Group II:</em> Target 75% to 100% (short-term securities and all other fixed-maturity securities)
										</p>
									</li>
								</ul>
							</div>

							<div>
								<p className="mb-0">
									<span className="text-[#F26721] font-late-serif font-wght-700">Financing&nbsp;</span> Maintain sufficient capital to support our business
								</p>
								<ul className="mt-0 mb-0 ml-5 list-disc">
									<li>Maintain debt below 30% of total capital at book value</li>
									<li>Neutralize dilution from equity-based compensation in the year of issuance through share repurchases</li>
									<li>Use under-leveraged capital to repurchase shares and pay dividends</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</Section>
			<div className="container py-12 md:py-24">
				<div className="grid gap-6 md:gap-0">
					{/* <div className="mr-auto max-w-[584px]">
						<div>
							<InlineClippedImage
								src={Connell_2015_Lakeside}
								alt="Art"
								dimensions={{
									width: 2592,
									height: 1944,
								}}
								clipPaths={[
									{
										initial: { x: '30%', y: '18%', width: '45%' },
										expanded: { x: '0%', y: '0%', width: '100%' },
									},
								]}
							/>
						</div>
					</div> */}
					<div className="ml-auto max-w-[584px] ">
						<div>
							<InlineClippedImage
								src={Connell_2017_Melanies_Place_PLACEHOLDER}
								alt="Art"
								dimensions={{
									width: 1152,
									height: 864,
								}}
								clipPaths={[
									{
										initial: {
											x: '48%',
											y: '16%',
											width: '45%',
										},
										expanded: {
											x: '0%',
											y: '0%',
											width: '100%',
										},
									},
								]}
							/>
						</div>
					</div>
				</div>
			</div>
			<Section backgroundColor="#F6FAF9">
				<div className="container pt-12 mb-12 md:pt-24 max-w-container-md">
					<h2 className="font-trade-compressed uppercase m-0 text-[#F05860] text-[1.875rem] font-bold tracking-widest">OBJECTIVES AND POLICIES SCORECARD</h2>
				</div>
				<div className="w-full max-w-[100vw]">
					<div className="container max-w-container-lg">
						<Q1ObjectivesPolicesScorecard2024 />
					</div>
				</div>
			</Section>
			<div className="py-12 md:py-24">
				<div className="">
					<div className="md:grid grid-cols-[1fr_1fr] gap-12 px-4 sm:px-6">
						<div className="md:max-w-[581px] w-full ml-auto">
							<div className="mb-12">
								<h2 className="font-trade-compressed uppercase m-0 text-[1.875rem] font-bold tracking-widest text-[#4B7B9D]">Our Business Model</h2>
								<p>For us, a 96 combined ratio is not a &ldquo;solve for&rdquo; variable in our business model equation, but rather a constant that provides direction to each product and marketing decision and a cultural tipping point that ensures zero ambiguity as to how to act in certain situations. Set at a level we believe creates a fair balance between attractive profitability and consumer competitiveness, it&apos;s deeply ingrained and central to our culture.</p>
								<p className="italic">With clarity as to our business constant, we seek to maximize all other important variables and support with appropriate axioms:</p>
								<p>
									<span className="font-late-serif text-[#F26721] font-wght-700">Grow as fast as we can</span> subject to our ability to provide high-quality service. Our preferred measure of growth is in customers, best measured by policies in force.
								</p>
								<p>
									<span className="font-late-serif text-[#F26721] font-wght-700">Extend policy life expectancy.</span> Our preference is for the flexibility of shorter policy periods, highlighting, however, the importance of retaining customers at policy renewal. As part of our Destination Era strategy, our focus is inclusive of all points throughout a customer&apos;s tenure and is a never-ending focus, tailored for every consumer segment.
								</p>
								<p className="italic">Clarity as to our objectives means other elements of the business model must be appropriately designed to strongly support, but not necessarily amplify, the risk of maximizing all things at the same time. Our articulation of our most critical investment objective is a good example:</p>
								<p>
									<span className="font-late-serif text-[#F26721] font-wght-700">Invest in a manner that does not constrain our ability to underwrite all the profitable insurance available to us at an efficient premiums-to-surplus leverage.</span> We often refer to underwriting capacity as the protected asset and for us it is a clear determination of where the risk of leverage is best allocated.
								</p>
								<p className="italic">The importance of net income, earnings per share, and return on equity is never lost on us, and we view achieving strong, long-term performance of these measures as stemming from our consistent focus on the primary elements of our business model.</p>
							</div>
							<h2 className="font-trade-compressed uppercase m-0 text-[1.875rem] font-bold tracking-widest text-[#4B7B9D]">Achievements</h2>
							<p>We are convinced that the best way to maximize shareholder value is to achieve our financial objectives and policies consistently. A shareholder who purchased 100 shares of Progressive for $1,800 in our first public stock offering on April 15, 1971, would have owned 208,602 shares, including dividend reinvestment, on December 31, 2024, with a market value of $49,983,125, for a 21.0% compounded annual return, compared to the 10.9% return achieved by investors in the S&P 500 during the same period. In the one, five, and ten years ending December 31, 2024, Progressive shareholders have realized compounded annual returns, including dividend reinvestment, of 51.4%, 29.9%, and 27.5%, respectively, compared to 25.0%, 14.5%, and 13.1% for the S&P 500.</p>
							<p>We have consistently paid dividends since we went public in 1971. Assuming dividends were not reinvested, a shareholder who bought 100 shares at the initial public offering would now hold 92,264 shares and would have received cumulative dividends of $2,259,359, including $106,104 in 2024. In addition to paying dividends, over the years when we have had adequate capital and believed it to be appropriate, we have repurchased our shares. As our Financial Policies state, we will repurchase shares to neutralize the dilution from equity-based compensation programs and return any under-leveraged capital to investors. During 2024, we repurchased 665,095 common shares. The total cost to repurchase these shares was $134 million, with an average cost of $201.32 per share. Since 1971, we have spent $9.8 billion repurchasing our shares, at an average cost of $8.09 per share.</p>
						</div>
						<div className="grid">
							<div className="max-w-[586px] mx-auto">
								<div>
									<InlineClippedImage
										src={Connell_2013_Ascend}
										alt="Art"
										dimensions={{
											width: 2016,
											height: 1512,
										}}
										clipPaths={[
											{
												initial: {
													x: '44%',
													y: '8%',
													width: '45%',
												},
												expanded: {
													x: '0%',
													y: '0%',
													width: '100%',
												},
											},
										]}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Q1ObjectivesPolicies;
